import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/social-media-management-agency-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/social-media-management-agency-colorado")({
  head: () => ({
    meta: [
      { title: "Social Media Management Agency: What You're Actually Paying For (2026)" },
      { name: "description", content: "Hiring a social media management agency? What real management includes, what it costs in Colorado, and why posting alone doesn't grow a business." },
      { property: "og:title", content: "Social Media Management Agency: What You're Actually Paying For" },
      { property: "og:description", content: "What real social media management includes, what it costs in Colorado, and why posting alone doesn't grow a business." },
      { property: "og:url", content: `${SITE_URL}/learn/social-media-management-agency-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Social media management explained",
        description: ARTICLE?.desc ?? "What a social media management agency should actually do.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/social-media-management-agency-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Marketing Strategy"
      read="8 min read"
      title="Social media management agency: what you're actually paying for"
      intro="Most social media management is someone posting three times a week and calling it strategy. Here's what real management includes, what it should cost in Colorado, and how to tell if your agency is growing your business or just filling a calendar."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Social media management" },
      ]}
      path="/learn/social-media-management-agency-colorado"
      faqs={[
        { q: "What does a social media management agency do?", a: "Real management covers strategy (what to post and why), content creation, scheduling, community replies, and reporting tied to business results — calls and leads, not just likes. If your agency only schedules posts, you're paying for a calendar, not management." },
        { q: "How much does social media management cost in Colorado?", a: "For small businesses, social media management is usually bundled with broader marketing. Our Growth plan at $1,650/month includes social alongside local SEO and AI optimization — social alone rarely justifies a big standalone fee for a local business." },
        { q: "Can social media actually bring in customers?", a: "Yes, but indirectly for most local businesses. Social builds trust and keeps you visible so when someone needs you — or checks you out after finding you on Google — you look active and real. We've gotten clients 10,000 to 100,000+ views a month, which feeds the whole pipeline." },
      ]}
    >
      <h2>What real management includes</h2>
      <ul>
        <li><strong>A plan tied to your business.</strong> What to post, where, and why — based on what your customers care about, not generic &ldquo;motivation Monday&rdquo; filler.</li>
        <li><strong>Content that looks like you.</strong> Your work, your crew, your jobs. Stock graphics with your logo fool nobody.</li>
        <li><strong>Consistency.</strong> The algorithm rewards showing up. A burst of posts followed by silence is worse than a steady drip.</li>
        <li><strong>Replies and engagement.</strong> Comments and messages answered. Social is a conversation, not a billboard.</li>
        <li><strong>Reporting you can understand.</strong> Views, reach, and — most importantly — whether any of it turned into calls.</li>
      </ul>

      <h2>What it costs in Colorado</h2>
      <p>
        Standalone social media management for a small business typically runs $500–$1,500 a
        month. Our take: social works best as part of a bigger plan, not on its own. Our{" "}
        <strong>Growth plan at $1,650/month</strong> bundles social media with local SEO,
        Google Maps, and AI/GEO optimization — so your social presence and your search presence
        feed each other. Compare plans on our <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>Red flags</h2>
      <ul>
        <li><strong>Vanity metrics as the whole report.</strong> Likes don't pay bills. Ask how social connects to leads.</li>
        <li><strong>Generic content.</strong> If the same post could run on any business's page in any state, it's not management — it's a content mill.</li>
        <li><strong>No access to your own accounts.</strong> Your pages, your logins, your audience. Never let an agency hold them hostage.</li>
      </ul>

      <h2>Where social fits for a local business</h2>
      <p>
        For most Colorado service businesses, the order is: Google Maps and search first (that's
        where buyers with intent are), social second (that's where trust is built). Social
        amplifies a strong search presence — it rarely replaces one. Read our guide on{" "}
        <Link to="/learn/social-media-marketing-small-business-colorado">social media marketing for small business</Link>{" "}
        for the full picture.
      </p>

      <h2>What to do next</h2>
      <p>
        Not sure if social is worth it for your business? Request a{" "}
        <Link to="/free-audit">free digital marketing audit</Link> — we'll look at your whole
        online presence and tell you honestly where social fits.
      </p>
    </ArticleLayout>
  );
}
